import { useState, useEffect } from "react";
import { useCustomerAuth } from "@/components/customer/CustomerAuthProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { 
  Heart, 
  Plus, 
  Link2, 
  Share2, 
  Clock, 
  Sparkles, 
  ExternalLink,
  Grid3X3,
  Upload,
  ShoppingBag,
  Gem,
  Crown
} from "lucide-react";
import { useLocalization } from "@/hooks/useLocalization";

// Mock data for preview
const mockWishlistItems = [
  {
    id: '1',
    category: 'dress',
    source: 'pinterest',
    title: 'Elegant A-Line Gown',
    thumb_url: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=400&h=600&fit=crop',
    full_url: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=800&h=1200&fit=crop',
    created_at: new Date().toISOString()
  },
  {
    id: '2',
    category: 'veil',
    source: 'instore',
    title: 'Cathedral Length Veil',
    thumb_url: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400',
    full_url: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=800',
    created_at: new Date().toISOString()
  },
  {
    id: '3',
    category: 'shoes',
    source: 'lookbook',
    title: 'Pearl Detail Heels',
    thumb_url: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=400',
    full_url: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=800',
    created_at: new Date().toISOString()
  },
  {
    id: '4',
    category: 'jewelry',
    source: 'upload',
    title: 'Vintage Pearl Earrings',
    thumb_url: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=400',
    full_url: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=800',
    created_at: new Date().toISOString()
  },
  {
    id: '5',
    category: 'dress',
    source: 'pinterest',
    title: 'Mermaid Lace Gown',
    thumb_url: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=400&h=600&fit=crop',
    full_url: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=800&h=1200&fit=crop',
    created_at: new Date().toISOString()
  },
  {
    id: '6',
    category: 'veil',
    source: 'pinterest',
    title: 'Boho Fingertip Veil',
    thumb_url: 'https://images.unsplash.com/photo-1596008194705-2091cd6764d4?w=400',
    full_url: 'https://images.unsplash.com/photo-1596008194705-2091cd6764d4?w=800',
    created_at: new Date().toISOString()
  }
];

const categoryColors = {
  dress: 'bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900/20 dark:text-pink-300',
  veil: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300',
  shoes: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300',
  jewelry: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300',
  other: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300'
};

const sourceIcons = {
  instore: '❤️',
  pinterest: '📎',
  lookbook: '✨',
  upload: '📸'
};

// Source labels will be handled by translations

const categoryIcons = {
  dress: Crown,
  veil: Sparkles,
  shoes: ShoppingBag,
  jewelry: Gem,
  other: Heart
};

export default function CustomerWishlist() {
  const { customer } = useCustomerAuth();
  const { t } = useLocalization();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [fullScreenPhoto, setFullScreenPhoto] = useState<string | null>(null);
  const [newItem, setNewItem] = useState({
    category: 'dress',
    source: 'upload',
    title: '',
    url: ''
  });

  useEffect(() => {
    // Simulate loading for coming soon state
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);

  const categories = [
    { value: 'all', label: t('all'), icon: Grid3X3 },
    { value: 'dress', label: t('dresses'), icon: Crown },
    { value: 'veil', label: t('veils'), icon: Sparkles },
    { value: 'shoes', label: t('shoes'), icon: ShoppingBag },
    { value: 'jewelry', label: t('jewelry'), icon: Gem }
  ];

  const filteredItems = selectedCategory === 'all' 
    ? mockWishlistItems 
    : mockWishlistItems.filter(item => item.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">{t('loading_wishlist')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      <div className="space-y-6">
        {/* Coming Soon Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="h-6 w-6 text-primary animate-pulse" />
            <h1 className="text-2xl font-semibold text-foreground">{t('my_wishlist')}</h1>
            <Heart className="h-6 w-6 text-primary animate-pulse" />
          </div>
          <Badge variant="secondary" className="mb-4 bg-gradient-to-r from-pink-100 to-purple-100 dark:from-pink-900/20 dark:to-purple-900/20 border-pink-200 dark:border-pink-800">
            <Clock className="h-3 w-3 mr-1" />
            {t('wishlist_coming_soon')}
          </Badge>
          <p className="text-sm text-muted-foreground">
            {t('wishlist_description')}
          </p>
        </div>

        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          {/* Category Filter Chips */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Button
                  key={category.value}
                  variant={selectedCategory === category.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category.value)}
                  disabled
                  className="flex items-center gap-2 opacity-60"
                >
                  <IconComponent className="h-3 w-3" />
                  {category.label}
                </Button>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
              <DialogTrigger asChild>
                <Button disabled className="flex items-center gap-2 opacity-60">
                  <Plus className="h-4 w-4" />
                  {t('add_item')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('add_to_wishlist')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="category">{t('category')}</Label>
                    <Select value={newItem.category} onValueChange={(value) => setNewItem({...newItem, category: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dress">{t('dress')}</SelectItem>
                        <SelectItem value="veil">{t('veil')}</SelectItem>
                        <SelectItem value="shoes">{t('shoes')}</SelectItem>
                        <SelectItem value="jewelry">{t('jewelry')}</SelectItem>
                        <SelectItem value="other">{t('other')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="title">{t('title_optional')}</Label>
                    <Input 
                      id="title"
                      value={newItem.title}
                      onChange={(e) => setNewItem({...newItem, title: e.target.value})}
                      placeholder={t('title_placeholder')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="url">{t('image_url_upload')}</Label>
                    <Input 
                      id="url"
                      value={newItem.url}
                      onChange={(e) => setNewItem({...newItem, url: e.target.value})}
                      placeholder={t('image_url_placeholder')}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setAddModalOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button disabled>
                      <Upload className="h-4 w-4 mr-2" />
                      {t('add_to_wishlist_btn')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" disabled className="flex items-center gap-2 opacity-60">
                  <Share2 className="h-4 w-4" />
                  {t('share')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('share_your_wishlist')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t('share_description')}
                  </p>
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-mono">
                      <Link2 className="h-4 w-4" />
                      <span className="opacity-60">https://example.com/wl/abc123</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShareModalOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button disabled>
                      <Share2 className="h-4 w-4 mr-2" />
                      {t('create_share_link')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Masonry Gallery */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredItems.map((item) => {
            const CategoryIcon = categoryIcons[item.category as keyof typeof categoryIcons];
            return (
              <Card 
                key={item.id} 
                className="group cursor-pointer hover:shadow-lg transition-all duration-200 border-2 border-dashed border-primary/30 opacity-75"
                onClick={() => setFullScreenPhoto(item.full_url)}
              >
                <CardContent className="p-0">
                  <div className="relative aspect-[3/4] overflow-hidden rounded-t-lg">
                    <img
                      src={item.thumb_url}
                      alt={item.title || `${t(item.category)} ${t('inspiration')}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                    
                    {/* Source Badge */}
                    <Badge 
                      variant="secondary" 
                      className="absolute top-2 left-2 text-xs bg-white/90 backdrop-blur-sm"
                    >
                      {sourceIcons[item.source as keyof typeof sourceIcons]} {t(item.source === 'instore' ? 'in_store' : item.source as keyof typeof t)}
                    </Badge>

                    {/* Category Chip */}
                    <Badge 
                      className={`absolute top-2 right-2 text-xs ${categoryColors[item.category as keyof typeof categoryColors]}`}
                    >
                      <CategoryIcon className="h-3 w-3 mr-1" />
                      {t(item.category)}
                    </Badge>

                    {/* Hover Actions */}
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <ExternalLink className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  
                  {item.title && (
                    <div className="p-3">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('added')} {new Date(item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <Card className="border-2 border-dashed border-primary/30 opacity-75">
            <CardContent className="p-12 text-center">
              <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {selectedCategory === 'all' ? t('no_items_all') : t('no_items_yet').replace('{category}', selectedCategory)}
              </h3>
              <p className="text-muted-foreground mb-4">
                {t('wishlist_empty_description')}
              </p>
              <Button disabled className="opacity-60">
                <Plus className="h-4 w-4 mr-2" />
                {t('add_first_item')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Full Screen Photo Dialog */}
        <Dialog open={!!fullScreenPhoto} onOpenChange={() => setFullScreenPhoto(null)}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 border-0">
            <div className="relative w-full h-full flex items-center justify-center bg-black/90">
              <img
                src={fullScreenPhoto || ""}
                alt="Full screen inspiration"
                className="max-w-full max-h-full object-contain"
                onClick={() => setFullScreenPhoto(null)}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Notice */}
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <Sparkles className="h-4 w-4" />
              <p className="text-sm font-medium">
                {t('wishlist_preview_notice')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}